import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Params, RouterLink} from '@angular/router';
import {PrPageListDto} from '../../../../../generated-model/models';
import {resolveTotalElements} from '../../../../shared/commons/pagination.util';
import {PrService} from '../pr/pr.service';
import {PrAcfsComponent} from '../../../../layout/shared/pr-acfs/pr-acfs.component';

const DEFAULT_SORT = 'modifiedDate,desc';

interface CategoryChip {
  label: string;
  count: number | null;
  searchTerm: string;
}

@Component({
  selector: 'app-pr-public-landing',
  standalone: true,
  imports: [CommonModule, RouterLink, PrAcfsComponent],
  templateUrl: './pr-public-landing.html',
  styleUrl: './pr-public-landing.scss'
})
export class PrPublicLandingComponent implements OnInit, AfterViewInit {
  readonly sortModes = [
    {value: DEFAULT_SORT, label: 'ล่าสุด'},
    {value: 'viewCount,desc', label: 'ยอดนิยม'},
    {value: 'pageName,asc', label: 'ตามตัวอักษร'},
  ];

  readonly categories: CategoryChip[] = [
    {label: 'ทั้งหมด', count: null, searchTerm: ''},
    {label: 'มาตรฐานสินค้าเกษตร', count: null, searchTerm: 'มาตรฐาน'},
    {label: 'อาหารและความปลอดภัย', count: null, searchTerm: 'อาหาร'},
    {label: 'การส่งออก-นำเข้า', count: null, searchTerm: 'ส่งออก'},
    {label: 'เกษตรอินทรีย์', count: null, searchTerm: 'อินทรีย์'},
    {label: 'ใบรับรอง Q', count: null, searchTerm: 'รับรอง Q'},
    {label: 'ข่าวประชาสัมพันธ์', count: null, searchTerm: 'ข่าว'},
    {label: 'กิจกรรม & สัมมนา', count: null, searchTerm: 'กิจกรรม'},
  ];

  pages: PrPageListDto[] = [];
  searchQuery = '';
  sortBy = DEFAULT_SORT;
  activeCategory = 0;
  isLoading = false;
  isMobile = false;
  page = 1;
  pageSize = 9;
  collectionSize = 0;
  standardCount = 0;
  displayStandardCount = 0;
  businessCount = 0;
  displayBusinessCount = 0;
  @ViewChild('statsStrip') private statsStrip?: ElementRef<HTMLElement>;
  private statsInView = false;
  private standardLoaded = false;
  private businessLoaded = false;
  private countsAnimated = false;

  get featured(): PrPageListDto | null {
    return this.pages[0] ?? null;
  }

  get gridCards(): PrPageListDto[] {
    return this.pages.slice(1);
  }

  getCardCodes(item: PrPageListDto): string[] {
    return item.certificationCodes?.slice(0, 3) ?? [];
  }

  getExtraCodesCount(item: PrPageListDto): number {
    return Math.max(0, (item.certificationCodes?.length ?? 1) - 3);
  }

  get totalPages(): number {
    return Math.ceil(this.collectionSize / this.pageSize);
  }

  get pageRangeStart(): number {
    return (this.page - 1) * this.pageSize + 1;
  }

  get pageRangeEnd(): number {
    return Math.min(this.page * this.pageSize, this.collectionSize);
  }

  get paginationPages(): (number | '...')[] {
    const total = this.totalPages;
    const maxButtons = this.isMobile ? 5 : 7;
    if (total <= maxButtons) {
      return Array.from({length: total}, (_, i) => i + 1);
    }
    const neighbours = this.isMobile ? 0 : 1;
    const pages: (number | '...')[] = [1];
    if (this.page > 2 + neighbours) {
      pages.push('...');
    }
    const start = Math.max(2, this.page - neighbours);
    const end = Math.min(total - 1, this.page + neighbours);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (this.page < total - 1 - neighbours) {
      pages.push('...');
    }
    pages.push(total);
    return pages;
  }

  constructor(private readonly prService: PrService, private readonly cdr: ChangeDetectorRef, private readonly route: ActivatedRoute) {}

  ngOnInit(): void {
    this.updateViewport();
    const query = this.route.snapshot.queryParamMap.get('q');
    if (query) {
      this.searchQuery = query;
    }
    this.loadPages();
    this.loadStandardCount();
    this.loadBusinessCount();
  }

  ngAfterViewInit(): void {
    this.observeStatsStrip();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewport();
  }

  private updateViewport(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.isMobile = window.innerWidth <= 640;
  }

  loadPages(): void {
    this.isLoading = true;
    this.prService.getPublishedPages(this.page - 1, this.pageSize, this.searchQuery, this.sortBy).subscribe({
      next: (response) => {
        this.pages = response.content;
        this.collectionSize = resolveTotalElements(response);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.pages = [];
        this.collectionSize = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadStandardCount(): void {
    this.prService.getActiveStandardCount().subscribe({
      next: (count) => {
        this.standardCount = count ?? 0;
        this.standardLoaded = true;
        this.maybeAnimateCounts();
        this.cdr.detectChanges();
      },
      error: () => {
        this.standardLoaded = true;
        this.maybeAnimateCounts();
      }
    });
  }

  private loadBusinessCount(): void {
    this.prService.getActiveBusinessCount().subscribe({
      next: (count) => {
        this.businessCount = count ?? 0;
        this.businessLoaded = true;
        this.maybeAnimateCounts();
        this.cdr.detectChanges();
      },
      error: () => {
        this.businessLoaded = true;
        this.maybeAnimateCounts();
      }
    });
  }

  private observeStatsStrip(): void {
    const el = this.statsStrip?.nativeElement;
    if (!el || typeof IntersectionObserver === 'undefined') {
      this.statsInView = true;
      this.maybeAnimateCounts();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.statsInView = true;
          this.maybeAnimateCounts();
          observer.disconnect();
        }
      }
    }, {threshold: 0.35});
    observer.observe(el);
  }

  private maybeAnimateCounts(): void {
    if (!this.statsInView || !this.standardLoaded || !this.businessLoaded || this.countsAnimated) {
      return;
    }
    this.countsAnimated = true;
    this.animateValue(this.standardCount, (v) => (this.displayStandardCount = v));
    this.animateValue(this.businessCount, (v) => (this.displayBusinessCount = v));
  }

  private animateValue(target: number, setter: (value: number) => void): void {
    if (target <= 0) {
      setter(target);
      this.cdr.detectChanges();
      return;
    }
    const duration = 1200;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setter(Math.round(target * eased));
      this.cdr.detectChanges();
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }

  onSearch(): void {
    this.page = 1;
    this.loadPages();
  }

  onSortMode(value: string): void {
    this.sortBy = value;
    this.page = 1;
    this.loadPages();
  }

  onCategory(index: number): void {
    this.activeCategory = index;
    this.searchQuery = this.categories[index].searchTerm;
    this.page = 1;
    this.loadPages();
  }

  goToPage(p: number | '...'): void {
    if (p === '...' || p === this.page) {
      return;
    }
    this.page = p;
    this.loadPages();
  }

  prevPage(): void {
    if (this.page > 1) {
      this.page--;
      this.loadPages();
    }
  }

  nextPage(): void {
    if (this.page < this.totalPages) {
      this.page++;
      this.loadPages();
    }
  }

  get isHeadless(): boolean {
    return this.route.snapshot.data['headless'] === true;
  }

  get basePath(): string {
    return this.isHeadless ? '/pr-headless' : '/pr';
  }

  // "ดูทั้งหมด" opens the full list; headless stays inside the embed instead of the public site layout.
  get allPagesLink(): string[] {
    return this.isHeadless ? ['/pr-headless', 'list'] : ['/public', 'pr', 'list'];
  }

  // Carry the current search/category and sort so the full list continues what this section shows.
  get allPagesQueryParams(): Params {
    return {
      q: this.searchQuery.trim() || null,
      sort: this.sortBy === DEFAULT_SORT ? null : this.sortBy,
    };
  }

  getPageLink(item: PrPageListDto): string[] {
    return [this.basePath, item.runpage ?? ''];
  }

  getFallbackSubtitle(item: PrPageListDto): string {
    return item.businessNames?.[0] || item.pageCode || 'เพจประชาสัมพันธ์';
  }

  getProductDetailPreview(item: PrPageListDto): string {
    const text = item.productDetail || '';
    return text.replace(/[#*_`>\-[\]()]/g, '').replace(/\s+/g, ' ').trim();
  }

  formatThaiDate(value?: string | Date | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    return date.toLocaleDateString('th-TH', {day: 'numeric', month: 'short', year: 'numeric'});
  }

  formatThaiDateShort(value?: string | Date | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    return date.toLocaleDateString('th-TH', {day: 'numeric', month: 'short', year: 'numeric'});
  }
}
